/**
 * Sanitize DIY per-step asset references (kpiKey, hookKey, caseStudyKey, productLinkKeys)
 * against the current owner's catalog so clones/loads/saves never keep another user's keys.
 * Product link payloads use only trimmed canonical `admin_atoms.product_links[].name` strings.
 * Does not modify ctaStyleKey, strategicHook, messageFocus, or playbook-level fields.
 */

/**
 * @param {unknown} value
 * @param {number} count
 * @param {'kpi'|'hook'|'case_study'} kind
 */
function isValidPositionalAssetKey(value, count, kind) {
  if (value == null || value === '') return true;
  const s = String(value).trim();
  let re;
  if (kind === 'kpi') re = /^kpi_(\d+)$/;
  else if (kind === 'hook') re = /^hook_(\d+)$/;
  else if (kind === 'case_study') re = /^case_study_(\d+)$/;
  else return false;
  const m = s.match(re);
  if (!m) return false;
  const i = parseInt(m[1], 10);
  return Number.isInteger(i) && i >= 0 && i < count;
}

function logStrip(stepOrder, field, strippedValue, reason) {
  console.warn('[diy_form_asset_stripped]', {
    step_order: stepOrder,
    field,
    stripped_value: strippedValue,
    reason,
  });
}

function stepOrderOf(step, index) {
  const o = step?.order;
  if (o != null && Number.isFinite(Number(o))) return Number(o);
  return index + 1;
}

/**
 * Canonical trimmed names + alias map (legacy id/slug → name) for READ-time sanitization only.
 * @param {unknown[]} rows
 * @returns {{ productLinkNames: Set<string>, productLinkAliasToCanonical: Map<string, string> }}
 */
function buildProductLinkResolution(rows) {
  const productLinkNames = new Set();
  const productLinkAliasToCanonical = new Map();
  for (const row of rows || []) {
    if (!row || typeof row !== 'object') continue;
    const name = row.name != null ? String(row.name).trim() : '';
    if (!name) continue;
    productLinkNames.add(name);
    productLinkAliasToCanonical.set(name, name);
    const id = row.id != null ? String(row.id).trim() : '';
    if (id && id !== name) productLinkAliasToCanonical.set(id, name);
  }
  return { productLinkNames, productLinkAliasToCanonical };
}

/**
 * @typedef {{
 *   kpiCount: number,
 *   hookCount: number,
 *   caseStudyCount: number,
 *   productLinkNames: Set<string>,
 *   productLinkAliasToCanonical: Map<string, string>,
 * }} DiyStepAssetCatalog
 */

/**
 * Catalog from `/api/settings/personalization` (or equivalent) body.
 * @param {Record<string, unknown>|null|undefined} data
 * @returns {DiyStepAssetCatalog | null}
 */
export function buildDiyStepAssetCatalogFromPersonalization(data) {
  if (!data || typeof data !== 'object') return null;
  const kpis = /** @type {unknown[]} */ (data.kpis || []);
  const hooks = /** @type {unknown[]} */ (data.hooks || []);
  const caseRows =
    data.case_study_links ||
    data.caseStudies ||
    data.case_studies ||
    data.caseStudyLinks ||
    [];

  const plRows = /** @type {unknown[]} */ (data.product_links || data.productLinks || []);
  const { productLinkNames, productLinkAliasToCanonical } = buildProductLinkResolution(plRows);

  return {
    kpiCount: kpis.length,
    hookCount: hooks.length,
    caseStudyCount: /** @type {unknown[]} */ (caseRows || []).length,
    productLinkNames,
    productLinkAliasToCanonical,
  };
}

/**
 * Catalog from DIY filter-options `settings` (normalized rows; `name` set when API provided it).
 * @param {Record<string, unknown>|null|undefined} settings
 * @returns {DiyStepAssetCatalog | null}
 */
export function buildDiyStepAssetCatalogFromFormSettings(settings) {
  if (!settings || typeof settings !== 'object') return null;
  const { productLinkNames, productLinkAliasToCanonical } = buildProductLinkResolution(
    settings.productLinks || []
  );
  return {
    kpiCount: (settings.kpis || []).length,
    hookCount: (settings.hooks || []).length,
    caseStudyCount: (settings.caseStudies || []).length,
    productLinkNames,
    productLinkAliasToCanonical,
  };
}

/** @deprecated Use buildDiyStepAssetCatalogFromPersonalization */
export function buildDiyCatalogValidSetsFromPersonalizationResponse(data) {
  return buildDiyStepAssetCatalogFromPersonalization(data);
}

/** @deprecated Use buildDiyStepAssetCatalogFromFormSettings — second arg ignored (CTA is not reconciled). */
export function buildDiyCatalogValidSetsFromFormSettings(settings, _ctaStyles) {
  return buildDiyStepAssetCatalogFromFormSettings(settings);
}

/**
 * @param {unknown[]} keys
 * @param {DiyStepAssetCatalog} catalog
 * @param {number} stepOrder
 * @param {boolean} logWarnings
 * @returns {string[]} canonical trimmed names only
 */
function sanitizeProductLinkKeys(keys, catalog, stepOrder, logWarnings) {
  const nameSet = catalog.productLinkNames;
  const alias = catalog.productLinkAliasToCanonical;
  const arr = Array.isArray(keys) ? keys : [];
  if (nameSet.size === 0) {
    for (const x of arr) {
      const t = x == null ? '' : String(x).trim();
      if (t !== '' && logWarnings) logStrip(stepOrder, 'productLinkKeys', t, 'not_in_catalog');
    }
    return [];
  }
  const out = [];
  for (const x of arr) {
    const t = x == null ? '' : String(x).trim();
    if (t === '') continue;
    const canon = alias.get(t) ?? (nameSet.has(t) ? t : null);
    if (canon != null && nameSet.has(canon)) {
      if (!out.includes(canon)) out.push(canon);
    } else if (logWarnings) {
      logStrip(stepOrder, 'productLinkKeys', t, 'not_in_catalog');
    }
  }
  return out;
}

/**
 * @param {unknown} step
 * @param {DiyStepAssetCatalog} catalog
 * @param {number} stepOrder
 * @param {{ logWarnings?: boolean }} [options]
 * @returns {{ step: Record<string, unknown>, changed: boolean }}
 */
export function sanitizeOneDiyStepAgainstCatalog(step, catalog, stepOrder, options = {}) {
  const { logWarnings = true } = options;
  let changed = false;
  const copy = { ...(step && typeof step === 'object' ? step : {}) };

  const stripPositional = (field, count, kind) => {
    const v = copy[field];
    if (v == null || v === '') return;
    if (!isValidPositionalAssetKey(v, count, kind)) {
      if (logWarnings) logStrip(stepOrder, field, String(v).trim(), 'not_in_catalog');
      copy[field] = null;
      changed = true;
    }
  };

  stripPositional('kpiKey', catalog.kpiCount, 'kpi');
  stripPositional('hookKey', catalog.hookCount, 'hook');
  stripPositional('caseStudyKey', catalog.caseStudyCount, 'case_study');

  const prevPl = copy.productLinkKeys;
  const nextPl = sanitizeProductLinkKeys(prevPl, catalog, stepOrder, logWarnings);
  const plChanged = JSON.stringify(prevPl || []) !== JSON.stringify(nextPl);
  if (plChanged) {
    copy.productLinkKeys = nextPl;
    changed = true;
  }

  return { step: copy, changed };
}

/**
 * @param {unknown[]} steps
 * @param {DiyStepAssetCatalog|null|undefined} catalog
 * @param {{ logWarnings?: boolean }} [options]
 * @returns {{ steps: unknown[], changed: boolean }}
 */
export function sanitizeDiyStepsArray(steps, catalog, options = {}) {
  if (!catalog || !Array.isArray(steps)) return { steps: steps || [], changed: false };
  let changed = false;
  const next = steps.map((step, index) => {
    const ord = stepOrderOf(step, index);
    const { step: s, changed: c } = sanitizeOneDiyStepAgainstCatalog(step, catalog, ord, options);
    if (c) changed = true;
    return s;
  });
  return { steps: next, changed };
}

/**
 * @param {Record<string, unknown>} formValues
 * @param {{ settings?: Record<string, unknown> }|null|undefined} filterOptions
 * @param {{ logWarnings?: boolean }} [options]
 */
export function sanitizeDiyFormValuesAgainstCatalog(formValues, filterOptions, options = {}) {
  if (!formValues || typeof formValues !== 'object') return formValues;
  if (!filterOptions?.settings) return formValues;
  const catalog = buildDiyStepAssetCatalogFromFormSettings(filterOptions.settings);
  if (!catalog) return formValues;
  const { steps, changed } = sanitizeDiyStepsArray(formValues.steps, catalog, options);
  if (!changed) return formValues;
  return { ...formValues, steps };
}
